import React, { useEffect, useId, useRef, useState } from "react";
import {
  Film,
  Heart,
  Lightbulb,
  Loader,
  PlusCircle,
  Tv,
  X,
} from "lucide-react";
import {
  LibraryPage,
  EmptyState,
  Poster,
  Chips,
  isSeries,
  splitGenres,
} from "./LibraryLayout";

const SEARCH_DEBOUNCE_MS = 300;

// Dropdown thumbnails only need a tiny image.
const tinyPoster = (url) =>
  url ? url.replace(/\/t\/p\/w\d+\//, "/t/p/w92/") : "";

function SearchBox({ search }) {
  const {
    query,
    suggestions,
    open,
    loading,
    onQueryChange,
    onSearch,
    onFocus,
    onClose,
    onSelect,
    onSubmit,
  } = search;

  const uid = useId();
  const listId = `${uid}-list`;
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const [active, setActive] = useState(-1);

  const expanded = open && suggestions.length > 0;

  // Close when clicking anywhere outside the search box.
  useEffect(() => {
    if (!expanded) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [expanded, onClose]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);
  useEffect(() => setActive(-1), [suggestions]);

  const handleChange = (e) => {
    const value = e.target.value;
    onQueryChange(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch(value), SEARCH_DEBOUNCE_MS);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown" && suggestions.length) {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp" && suggestions.length) {
      e.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Escape") {
      onClose();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (expanded) onSelect(suggestions[active >= 0 ? active : 0]);
      else onSubmit();
    }
  };

  const hasText = query.trim().length > 0;

  return (
    <section className="nq-search" aria-labelledby={`${uid}-title`}>
      <h2 className="nq-search__title" id={`${uid}-title`}>
        <PlusCircle size={22} aria-hidden="true" />
        Add a movie or show you love
      </h2>

      <div className="nq-search__row">
        <div className="nq-search__field" ref={wrapRef}>
          <input
            type="text"
            className="nq-search__input"
            placeholder="Search for movies or shows…"
            aria-label="Search for a movie or show to add"
            role="combobox"
            aria-expanded={expanded}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              active >= 0 ? `${uid}-opt-${active}` : undefined
            }
            autoComplete="off"
            value={query}
            onChange={handleChange}
            onFocus={onFocus}
            onKeyDown={handleKeyDown}
          />

          {loading ? (
            <Loader
              size={20}
              className="nq-search__spinner"
              aria-hidden="true"
            />
          ) : null}

          {expanded ? (
            <ul className="nq-search__list" id={listId} role="listbox">
              {suggestions.map((s, i) => (
                <li
                  key={`${s.title}-${s.year}-${i}`}
                  id={`${uid}-opt-${i}`}
                  role="option"
                  aria-selected={i === active}
                  className="nq-search__option"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => onSelect(s)}
                >
                  <span className="nq-search__thumb">
                    {s.posterUrl ? (
                      <img
                        src={tinyPoster(s.posterUrl)}
                        alt=""
                        loading="lazy"
                      />
                    ) : isSeries(s.type) ? (
                      <Tv size={18} aria-hidden="true" />
                    ) : (
                      <Film size={18} aria-hidden="true" />
                    )}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      className="nq-search__name"
                      style={{ display: "block" }}
                    >
                      {s.title}
                    </span>
                    <span className="nq-search__sub">
                      {[
                        s.type,
                        s.year !== "N/A" ? s.year : null,
                        s.imdbRating !== "N/A"
                          ? `${s.imdbRating} rating`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                  <PlusCircle size={20} color="#4338ca" aria-hidden="true" />
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <button
          type="button"
          className="nq-btn nq-btn--primary"
          style={{ minHeight: 48, paddingInline: "1.5rem" }}
          disabled={!hasText}
          onClick={() =>
            expanded
              ? onSelect(suggestions[active >= 0 ? active : 0])
              : onSubmit()
          }
        >
          Add
        </button>
      </div>

      <p className="nq-search__hint">
        <Lightbulb size={16} aria-hidden="true" />
        Titles you like make your recommendations better.
      </p>
    </section>
  );
}

export default function LikedView({
  movies,
  search,
  onUnlike,
  onBack,
  toast,
  overlay,
}) {
  const count = movies.length;
  const subtitle =
    count === 0
      ? null
      : `${count} ${count === 1 ? "title" : "titles"} you love`;

  return (
    <LibraryPage
      title="Liked movies & shows"
      icon={<Heart size={36} fill="currentColor" aria-hidden="true" />}
      subtitle={subtitle}
      onBack={onBack}
      toast={toast}
      overlay={overlay}
    >
      <SearchBox search={search} />

      {count === 0 ? (
        <EmptyState
          icon={<Heart size={56} />}
          title="No liked titles yet"
          text="Like something from your recommendations, or search for a favourite above."
        />
      ) : (
        <ul className="nq-lib__grid nq-lib__grid--posters">
          {[...movies].reverse().map((m) => (
            <li key={m.title} className="nq-card nq-card--poster">
              <Poster url={m.posterUrl} type={m.type} variant="full" />
              <div className="nq-card__body">
                <h3 className="nq-card__title">{m.title}</h3>
                <p className="nq-card__meta">
                  {[
                    m.type,
                    m.year,
                    m.imdbRating && m.imdbRating !== "N/A"
                      ? `${m.imdbRating} rating`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <Chips items={splitGenres(m.genre)} />
                <div className="nq-card__actions">
                  <button
                    type="button"
                    className="nq-btn nq-btn--ghost"
                    style={{ width: "100%" }}
                    onClick={() => onUnlike(m.title)}
                    aria-label={`Unlike ${m.title}`}
                  >
                    <X size={16} aria-hidden="true" />
                    Unlike
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </LibraryPage>
  );
}
